import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue } from 'framer-motion';
import { Arrow } from '@/assets/images';
import styles from './Slider.module.css';

type SliderProps = {
  className?: string;
  inverted?: boolean;
  text?: string;
  label?: string;
  isSuccess?: boolean;
  onReady?: () => void;
  translations: Record<string, string>;
};

const Slider = ({
  className,
  inverted = false,
  text,
  label,
  isSuccess,
  onReady,
  translations
}: SliderProps) => {
  const hasTriggered = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDone, setIsDone] = useState(false);
  const [constraints, setConstraints] = useState({ left: 0, right: 0 });
  const [progress, setProgress] = useState(0);
  const [activeStatus, setActiveStatus] = useState<boolean | null>(null);
  const [showStatus, setShowStatus] = useState(false);

  const getStatusText = (success: boolean | null) => {
    if (success === true) return translations.answerscorrect;
    if (success === false) return translations.answersincorrect;
    return '';
  };
  
  const x = useMotionValue(0);

  useEffect(() => {
    if (isSuccess !== undefined) {
      setActiveStatus(isSuccess);
      const timer = setTimeout(() => {
        setShowStatus(true);
      }, 400); 
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isSuccess]);

  useEffect(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    const handleWidth = 68;
    const travelDistance = containerWidth - handleWidth - 12;
    
    setConstraints(inverted 
      ? { left: -travelDistance, right: 0 } 
      : { left: 0, right: travelDistance }
    );
  }, [inverted]);

  useEffect(() => {
    const target = inverted ? constraints.left : constraints.right;
    if (target === 0) return;

    const unsubscribe = x.on("change", (latestX) => {
      const currentProgress = Math.min(100, Math.max(0, (Math.abs(latestX) / Math.abs(target)) * 100));
      setProgress(currentProgress);

      const threshold = Math.abs(target) * 0.98;
      if (Math.abs(latestX) >= threshold && !hasTriggered.current) {
        hasTriggered.current = true;
        setIsDone(true);
        onReady?.();
      }
    });
    return () => unsubscribe();
  }, [x, onReady, inverted, constraints]);

  return (
    <div 
      ref={containerRef} 
      className={`${styles.wrap} ${inverted ? styles.inverted : ''} ${className ?? ''}`}
    >
      <div 
        className={styles.progressOverlay}
        style={{ 
          opacity: progress / 100,
          background: showStatus && activeStatus !== null ? 'transparent' : undefined
        }}
      />

      {showStatus && activeStatus !== null && (
        <div 
          className={`${styles.statusLabel} ${activeStatus ? styles.statusSuccess : styles.statusFail} ${styles.showStatus}`}
        >
          <span className={styles.statusIcon}>{activeStatus ? '✓' : '✕'}</span>
          <span className={styles.statusText}>
            {getStatusText(activeStatus)}
          </span>
        </div>
      )}

      <div className={styles.sliderText}>
        {text}
      </div>

      <motion.div
        drag="x"
        dragConstraints={constraints}
        dragElastic={0.1}
        dragMomentum={false}
        dragListener={true}
        dragPropagation={true}
        onDragStart={() => {
          document.body.style.userSelect = 'none';
        }}
        onDragEnd={() => {
          document.body.style.userSelect = 'auto';
        }}
        style={{ x, touchAction: 'none' }}
        className={`${styles.handle} ${isDone ? styles.handleDone : ''}`}
      >
        <div 
          className={styles.handleBackground}
          style={{ opacity: 1 - (progress / 100) }}
        />
        {label ? (
          <span className={styles.label}>{label}</span>
        ) : (
          <Arrow className={`${styles.arrow} ${inverted ? styles.arrowInverted : ''}`} />
        )}
      </motion.div>
    </div>
  );
};

export default Slider;